'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Clock, X } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useStoreStatus } from '@/hooks/useStoreStatus';
import { DAY_NAMES, getNextOpening, isOpenAt } from '@/lib/storeSchedule';

const DISMISS_KEY = 'storeClosedBannerDismissed';
const HEIGHT_VAR = '--store-banner-h';

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export default function StoreClosedBanner() {
  const pathname = usePathname();
  const { language } = useLanguage();
  const { status, loading } = useStoreStatus();
  const [now, setNow] = useState(() => new Date());
  const [dismissed, setDismissed] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  // Re-evaluate every minute so the banner appears/disappears at opening and closing time
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDismissed(readDismissed());
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const manuallyClosed = !!status && !status.isOpen;
  const closedBySchedule = !!status && !isOpenAt(status.schedule, now);
  const visible =
    !loading &&
    !dismissed &&
    !pathname?.startsWith('/admin') &&
    (manuallyClosed || closedBySchedule);

  // Expose the banner height so fixed headers and page content can sit below it
  useEffect(() => {
    const root = document.documentElement;
    const el = bannerRef.current;
    if (!visible || !el) {
      root.style.removeProperty(HEIGHT_VAR);
      return;
    }
    const update = () => root.style.setProperty(HEIGHT_VAR, `${el.offsetHeight}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty(HEIGHT_VAR);
    };
  }, [visible]);

  if (!visible || !status) return null;

  const ro = language === 'ro';

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore — the banner simply comes back on the next page load
    }
  };

  let details: string;
  if (manuallyClosed) {
    details = status.closureMessage || (ro
      ? 'Nu acceptăm comenzi în acest moment. Vă rugăm să reveniți mai târziu.'
      : 'We are not accepting orders at this time. Please check back later.');
  } else {
    const next = getNextOpening(status.schedule, now);
    let reopen = '';
    if (next) {
      const when =
        next.relative === 'today' ? (ro ? 'astăzi' : 'today')
        : next.relative === 'tomorrow' ? (ro ? 'mâine' : 'tomorrow')
        : ro ? DAY_NAMES.ro[next.dayIndex].toLowerCase() : `on ${DAY_NAMES.en[next.dayIndex]}`;
      reopen = ro ? `Revenim ${when} la ${next.time}. ` : `We reopen ${when} at ${next.time}. `;
    }
    details = reopen + (ro
      ? 'Poți plasa comanda online, o procesăm la deschidere.'
      : 'You can still order online — we will process it when we open.');
  }

  return (
    <div
      ref={bannerRef}
      role="status"
      className="organic fixed top-0 left-0 right-0"
      style={{
        zIndex: 400,
        background: 'var(--color-accent-800)',
        color: '#fff',
        fontSize: 13.5,
        lineHeight: 1.4,
      }}
    >
      <div
        className="max-w-7xl mx-auto flex items-center"
        style={{ gap: 10, padding: '8px clamp(16px, 4vw, 48px)' }}
      >
        <Clock className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <p style={{ margin: 0, flex: 1 }}>
          <strong>{ro ? 'Momentan magazinul este închis.' : 'The shop is currently closed.'}</strong>{' '}
          {details}
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={ro ? 'Ascunde mesajul' : 'Dismiss message'}
          className="flex-shrink-0 rounded-full flex items-center justify-center hover:bg-white/15 transition-colors"
          style={{ width: 28, height: 28, color: '#fff' }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
